import { HANDOVER_PARAMS } from './handoverParams';

/**
 * The page the user was actually trying to reach when a guard sent them to sign in.
 *
 * `returnUrl` on the sign-in link is the primary mechanism and it works: the SIYA login
 * app stores it (sessionStorage `loginReturnUrl`), and on a successful sign-in — or
 * immediately, when the visitor still has a main-app session — it navigates back to it
 * with `authToken` appended. That is what makes an emailed deep link such as
 * /class?imo=9735062 survive a round trip through login.
 *
 * It does NOT survive two paths, and this module is the fallback for both:
 *   1. `?logout=true` (the forced re-authentication used on the permission cards). Login's
 *      logout branch runs first and does `location.replace(origin + pathname)`, throwing
 *      away the query string before anything reads returnUrl.
 *   2. Users routed to chat or to the micro-apps root after signing in.
 *
 * The deep link is kept on the micro-apps origin — the login app's `localStorage.clear()`
 * cannot touch it — and /index restores it when the user next arrives through the root.
 *
 * Short-lived on purpose: this is "where you were headed a minute ago", not a bookmark.
 * A stale entry would hijack an unrelated visit days later.
 */

const KEY = 'pendingDeepLink';
const TTL_MS = 30 * 60 * 1000;

// The value read on this page load, kept so a second call returns the same answer rather
// than null. React StrictMode invokes effects twice (reactStrictMode is on), and the
// redirect on /index is driven from an effect: without this, the first call consumed the
// deep link and the second saw nothing, so the page fell through to the last-visited
// route and the destination was silently lost. `undefined` = not read yet, distinct from
// a read that legitimately found nothing.
let consumedThisLoad: string | null | undefined;

/**
 * Normalise to a same-origin relative path, or null when the target is not one we should
 * ever navigate to automatically.
 */
function sanitise(target: string): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const url = new URL(target, window.location.origin);

    // Never restore anything off this origin — the stored value is later fed straight to
    // router.replace(), so an absolute URL here would be an open redirect.
    if (url.origin !== window.location.origin) return null;

    // The credential hand-off params are stripped for the same reason AuthGuard strips
    // them from the address bar: a JWT does not belong in storage we hand back to a URL,
    // and by the time this is restored it is long expired anyway.
    for (const param of HANDOVER_PARAMS) url.searchParams.delete(param);

    // The root is where the restore happens, so storing it would send /index to itself.
    if (url.pathname === '/' || url.pathname === '/index') return null;

    const path = `${url.pathname}${url.search}${url.hash}`;
    // '//host' is protocol-relative — same open-redirect hazard as above.
    return path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}

/** Remember where the user was headed. Defaults to the page they are on. */
export function savePendingDeepLink(target?: string): void {
  if (typeof window === 'undefined') return;

  const path = sanitise(target ?? window.location.href);
  if (!path) return;

  // A newly saved destination supersedes anything already read on this page load, which
  // matters under client-side navigation: the module (and its cache) outlives the route.
  consumedThisLoad = undefined;

  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Storage disabled/full — the returnUrl round trip is still in play, so this is a
    // degraded fallback, not a failure worth surfacing.
  }
}

/**
 * The remembered deep link, cleared from storage as it is read. Repeat calls within the
 * same page load return the same answer (see consumedThisLoad).
 */
export function consumePendingDeepLink(): string | null {
  if (typeof window === 'undefined') return null;
  if (consumedThisLoad !== undefined) return consumedThisLoad;

  try {
    const raw = localStorage.getItem(KEY);
    // Cleared as it is read: a restore that fails (no permission, app retired) must not be
    // retried on every later visit.
    localStorage.removeItem(KEY);

    let path: string | undefined;
    let at: number | undefined;
    if (raw) ({ path, at } = JSON.parse(raw) as { path?: string; at?: number });

    consumedThisLoad =
      path && typeof at === 'number' && Date.now() - at <= TTL_MS ? sanitise(path) : null;
    return consumedThisLoad;
  } catch {
    consumedThisLoad = null;
    return null;
  }
}

/** Drop the remembered deep link — the user got where they were going. */
export function clearPendingDeepLink(): void {
  if (typeof window === 'undefined') return;
  // Also mark this load as read: /index must not restore a link the user has already
  // reached by another route.
  consumedThisLoad = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Storage unavailable — the in-memory flag above already stops the restore.
  }
}
